import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router";
import {
  IonPage,
  IonContent,
  IonIcon,
} from "@ionic/react";
import {
  call,
  chatbubbleEllipses,
  swapHorizontalOutline,
  alertCircleOutline,
  timeOutline,
  chevronForwardOutline,
  closeOutline,
  checkmarkCircleOutline,
  bedOutline,
  peopleOutline,
  shieldCheckmarkOutline,
} from "ionicons/icons";
import "./Home.scss";
import { utilService } from "../../services/utilService";
import { loggedInUserService } from "../../services/loggedInUser.service";
import { StorageService } from "../../services/storage.service";
import { CONSTANTS } from "../../constants";
import { CallService } from "../../components/Shared/Call/Call.service";
import { pushNotificationService } from "../../services/PushNotification.service";
import EmtHomeComponent from "../EmtHome/EmtHome";
import { useTranslation } from "react-i18next";
import i18n from "../../locales/i18n/i18n";
import { homePageService } from "./Home.service";
import { HomePageParams } from "./Home.model";
import { MyPatientsService } from "../MyPatients/MyPatientsService";
import { NotificationsService } from "../Notifications/NotificationsService";
import { NOTIFICATION_TYPES } from "../Notifications/NotificationsConstants";
import { doctorsListService } from "../../components/Shared/DoctorList/doctor-list-service";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const NEEDS_YOU_LIMIT = 5;

interface DoctorOnCall {
  id: string;
  name: string;
  initials: string;
  specialty: string;
  role: string;
  timeRange: string;
  shiftType: string;
  status: "available" | "busy" | "unassigned";
  phone?: string;
  isSelf?: boolean;
}

interface PatientItem {
  id: string;
  bed: string;
  name: string;
  department: string;
  codeStatus: string;
  nurse: string;
  alertText?: string;
}

interface NeedItem {
  id: string;
  type: "cover" | "patient_alert" | "rota";
  iconType: "purple" | "red" | "amber";
  title: string;
  meta: string;
  status: "pending" | "accepted" | "declined" | "acknowledged";
  rawData?: any;
}

const Home: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation(["home", "common"]);

  const [isEMTUser, setIsEMTUser] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>({});
  const currentUserRef = useRef<any>(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);
  const [unreachableUntil, setUnreachableUntil] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<
    "none" | "thisShift" | "handover" | "rotaLead" | "callConfirm"
  >("none");
  const [activeCallTarget, setActiveCallTarget] = useState<string>("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [rotaLeadNote, setRotaLeadNote] = useState<string>("");

  // Loading flags
  const [isLoadingHome, setIsLoadingHome] = useState<boolean>(true);
  const [isLoadingPatients, setIsLoadingPatients] = useState<boolean>(true);
  const [isLoadingNeeds, setIsLoadingNeeds] = useState<boolean>(true);

  // Live Backend Data States
  const [doctorsOnCall, setDoctorsOnCall] = useState<DoctorOnCall[]>([]);
  const [totalGroupsCount, setTotalGroupsCount] = useState<number>(0);
  const [myPatients, setMyPatients] = useState<PatientItem[]>([]);
  const [needsItems, setNeedsItems] = useState<NeedItem[]>([]);

  // Shift details
  const [currentShiftHours, setCurrentShiftHours] = useState<string>("08:00 → 20:00");
  const [shiftProgressPercent, setShiftProgressPercent] = useState<number>(68);
  const [isOnCallActive, setIsOnCallActive] = useState<boolean>(true);

  // Header dynamic time
  const [formattedDateTime, setFormattedDateTime] = useState<string>("CHU Alger · Today");

  // Multi-language query check
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get("lang");
    if (lang && ["en", "fr"].includes(lang)) {
      i18n.changeLanguage(lang);
    }
  }, []);

  // Compute shift progress bar dynamically
  const calculateShiftProgress = useCallback((shiftStr?: string) => {
    const now = new Date();
    const currentHour = now.getHours() + now.getMinutes() / 60;
    
    // Default 08:00 - 20:00 (12-hour day shift)
    let startHour = 8;
    let endHour = 20;

    if (shiftStr && shiftStr.includes("→")) {
      const parts = shiftStr.split("→").map((s) => s.trim());
      const parseH = (p: string) => {
        const matches = p.match(/(\d{1,2}):(\d{2})/);
        if (matches) return parseInt(matches[1], 10) + parseInt(matches[2], 10) / 60;
        return null;
      };
      const sH = parseH(parts[0]);
      const eH = parseH(parts[1]);
      if (sH !== null && eH !== null) {
        startHour = sH;
        endHour = eH > sH ? eH : eH + 24;
      }
    }

    if (currentHour >= startHour && currentHour <= endHour) {
      const pct = Math.round(((currentHour - startHour) / (endHour - startHour)) * 100);
      setShiftProgressPercent(Math.min(100, Math.max(0, pct)));
      setIsOnCallActive(true);
    } else {
      // If outside shift window
      setShiftProgressPercent(100);
      setIsOnCallActive(false);
    }
  }, []);

  // Update dynamic time header
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const options: Intl.DateTimeFormatOptions = {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      };
      const formatted = now.toLocaleDateString("en-US", options);
      const hospital = currentUser?.hospitalName || "CHU Alger";
      setFormattedDateTime(`${hospital} · ${formatted}`);
    };
    updateDateTime();
    const timer = setInterval(updateDateTime, 30000);
    return () => clearInterval(timer);
  }, [currentUser]);

  // Toast notification helper
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  // 1. Fetch Home Page Roster & User data from backend
  const fetchHomePageData = useCallback(async () => {
    setIsLoadingHome(true);
    try {
      const params: HomePageParams = {
        hour: utilService.getCurrentHour().toString(),
        minute: utilService.getCurrentMinutes().toString(),
        date: utilService.getCurrentDate().toString(),
      };

      const respResult: any = await homePageService.getHomePageData(params);

      if (respResult?.isSuccess && respResult?.result) {
        const cUser = respResult.result.currentUser || {};
        setCurrentUser((prev: any) => ({ ...prev, ...cUser }));

        if (cUser.nextShiftHours) {
          setCurrentShiftHours(cUser.nextShiftHours);
          calculateShiftProgress(cUser.nextShiftHours);
        } else {
          calculateShiftProgress();
        }

        const otherUserList = respResult.result.otherUserList || {};
        const combined = { ...(otherUserList.own || {}), ...(otherUserList.added || {}) };
        const scheduleKeys = Object.keys(combined);
        setTotalGroupsCount(scheduleKeys.length);

        const rosterList: DoctorOnCall[] = [];
        const myGuid = utilService.getLoggedUserGuid();

        scheduleKeys.forEach((key) => {
          const scheduleObj = combined[key];
          const docs = scheduleObj?.data || [];

          if (docs.length === 0) {
            // Unassigned shift slot
            rosterList.push({
              id: `unassigned-${key}`,
              name: "Nobody on call",
              initials: "!",
              specialty: key,
              role: "first call",
              timeRange: "08:00 → 20:00",
              shiftType: "Day",
              status: "unassigned",
            });
          } else {
            docs.forEach((doc: any, dIdx: number) => {
              const fullName = doc.first_name || doc.last_name
                ? `Dr ${doc.first_name || ""} ${doc.last_name || ""}`.trim()
                : doc.user_name || `Doctor ${dIdx + 1}`;
              const initials = (
                (doc.first_name?.[0] || "") + (doc.last_name?.[0] || "")
              ).toUpperCase() || "DR";
              const isSelf = doc.guid === myGuid;

              rosterList.push({
                id: doc.guid || `${key}-${dIdx}`,
                name: fullName,
                initials: initials,
                specialty: doc.speciality?.[0]?.name || doc.user_specialties || key,
                role: doc.role || "first call",
                timeRange: doc.shift_time || "08:00 → 20:00",
                shiftType: doc.shift_type || "Day",
                status: doc.available === false ? "busy" : "available",
                phone: doc.phone_number || doc.mobile,
                isSelf: isSelf,
              });
            });
          }
        });

        // If open network users can be appended
        try {
          const onParams = {
            reg_code: utilService.getNetwork(),
            search: "",
            site_id: "",
            specialty_id: "",
            page: 1,
          };
          const onResult: any = await doctorsListService.getONusersList(onParams);
          if (onResult?.isSuccess && onResult?.result?.[0]?.data?.length) {
            const onDocs = onResult.result[0].data;
            onDocs.forEach((d: any, idx: number) => {
              if (!rosterList.some((existing) => existing.id === d.guid)) {
                rosterList.push({
                  id: d.guid || `on-${idx}`,
                  name: `Dr ${d.first_name || ""} ${d.last_name || ""}`.trim(),
                  initials: ((d.first_name?.[0] || "") + (d.last_name?.[0] || "")).toUpperCase() || "DR",
                  specialty: d.speciality?.map((s: any) => s.name).join(", ") || "General",
                  role: "first call",
                  timeRange: "08:00 → 20:00",
                  shiftType: "Day",
                  status: "available",
                  phone: d.phone_number,
                  isSelf: d.guid === myGuid,
                });
              }
            });
          }
        } catch (e) {
          // Open network users optional fallback
        }

        setDoctorsOnCall(rosterList);
      } else {
        setDoctorsOnCall([]);
        setTotalGroupsCount(0);
      }
    } catch (err) {
      console.warn("Failed to load home page roster data from backend:", err);
      setDoctorsOnCall([]);
      setTotalGroupsCount(0);
    } finally {
      setIsLoadingHome(false);
    }
  }, [calculateShiftProgress]);

  // 2. Fetch My Patients from backend
  const fetchMyPatients = useCallback(async () => {
    setIsLoadingPatients(true);
    try {
      const now = new Date();
      const reqParams = {
        reg_code: utilService.getNetwork(),
        hour: now.getHours(),
        minute: now.getMinutes(),
        date: `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`,
      };

      const resp: any = await MyPatientsService.getPatients(reqParams);

      if (resp?.isSuccess && resp?.result?.length) {
        const mapped: PatientItem[] = resp.result.map((item: any, idx: number) => ({
          id: item.id || item.patientDetails?.id || String(idx),
          bed: item.bedNumber || item.patientDetails?.bedNumber || `Bed ${101 + idx}`,
          name: item.patientDetails?.patient_name || item.name || "Patient",
          department: item.unitName || item.unit?.unitName || currentUserRef.current?.departmentName || "General",
          codeStatus: item.patientDetails?.code_status || "code status full",
          nurse: item.nurseContactDetails?.name || "Ward Nurse",
          alertText: item.called_nurse || item.alertText ? "Called twice, no answer" : undefined,
        }));
        setMyPatients(mapped);
      } else {
        setMyPatients([]);
      }
    } catch (err) {
      console.warn("Failed to load patients from backend:", err);
      setMyPatients([]);
    } finally {
      setIsLoadingPatients(false);
    }
  }, []);

  // 3. Fetch Needs You (Notifications & Coverage Requests) from backend
  const fetchNeedsYou = useCallback(async () => {
    setIsLoadingNeeds(true);
    try {
      const reqParams = {
        guid: utilService.getLoggedUserGuid(),
        page: 1,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        "cat-list": JSON.stringify([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
        reg_code: utilService.getNetwork(),
      };

      const resp: any = await NotificationsService.getNotifications(reqParams);

      if (resp?.isSuccess && resp?.result?.[0]?.data?.length) {
        const notifs = resp.result[0].data;
        const latestFirst = [...notifs].sort(
          (a: any, b: any) =>
            new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
        );

        // "Your next schedule is on MM-DD-YYYY ..." reminders become irrelevant
        // once that date has passed, and the backend re-sends the same reminder
        // on a schedule, so without this the list fills up with repeats of one
        // stale item and crowds out anything else that needs attention.
        const seenMessages = new Set<string>();
        const relevantNotifs = latestFirst.filter((notif: any) => {
          const dateMatch = notif.message?.match(/(\d{2})-(\d{2})-(\d{4})/);
          if (dateMatch) {
            const [, month, day, year] = dateMatch;
            const reminderDate = new Date(Number(year), Number(month) - 1, Number(day));
            if (reminderDate.getTime() < Date.now()) {
              return false;
            }
          }
          if (seenMessages.has(notif.message)) {
            return false;
          }
          seenMessages.add(notif.message);
          return true;
        });

        const mappedNeeds: NeedItem[] = relevantNotifs
          .slice(0, NEEDS_YOU_LIMIT)
          .map((notif: any, idx: number) => {
            const category = Number(notif.category?.id ?? NOTIFICATION_TYPES.INFO);

            const isShiftReq = [
              NOTIFICATION_TYPES.CONFIRM,
              NOTIFICATION_TYPES.UNASSIGNED,
              NOTIFICATION_TYPES.UNASSIGNED_CONFIRM,
              NOTIFICATION_TYPES.AUTOSYNC,
            ].includes(category);
            const isUrgent = [
              NOTIFICATION_TYPES.EMERGENCY,
              NOTIFICATION_TYPES.MISSED_CALL,
            ].includes(category);

            let type: "cover" | "patient_alert" | "rota" = "rota";
            let iconType: "purple" | "red" | "amber" = "amber";

            if (isShiftReq) {
              type = "cover";
              iconType = "purple";
            } else if (isUrgent) {
              type = "patient_alert";
              iconType = "red";
            }

            return {
              id: notif.notify_guid || `need-${idx}`,
              type: type,
              iconType: iconType,
              title: notif.message || "Notification item",
              meta: notif.created_on ? dayjs(notif.created_on).fromNow() : "Just now",
              status: "pending",
              rawData: notif,
            };
          });
        setNeedsItems(mappedNeeds);
      } else {
        setNeedsItems([]);
      }
    } catch (err) {
      console.warn("Failed to load notifications from backend:", err);
      setNeedsItems([]);
    } finally {
      setIsLoadingNeeds(false);
    }
  }, []);

  // Initial Data Fetching & User subscriptions
  useEffect(() => {
    pushNotificationService.getToken();

    const sub = loggedInUserService.loggedInUserData.subscribe((data: any) => {
      if (data?.userData) {
        setCurrentUser(data.userData);
        if (data.userData.roles) {
          const isEmt = data.userData.roles.some(
            (r: any) => r.name === CONSTANTS.ROLES.EMT
          );
          setIsEMTUser(isEmt);
        }
      } else {
        const storedUser = StorageService.getStorageItem(
          CONSTANTS.STORAGE[1],
          "userData",
          true
        );
        if (storedUser) {
          setCurrentUser(storedUser);
        }
      }
    });

    fetchHomePageData();
    fetchMyPatients();
    fetchNeedsYou();

    return () => {
      sub.unsubscribe();
    };
  }, [fetchHomePageData, fetchMyPatients, fetchNeedsYou]);

  // Quick Action Handlers
  const handleToggleUnreachable = () => {
    if (unreachableUntil) {
      setUnreachableUntil(null);
      showToast("Availability resumed. You are receiving calls.");
    } else {
      const resumeTime = new Date(Date.now() + 20 * 60000);
      const hours = String(resumeTime.getHours()).padStart(2, "0");
      const mins = String(resumeTime.getMinutes()).padStart(2, "0");
      const timeStr = `${hours}:${mins}`;
      setUnreachableUntil(timeStr);
      showToast(`Set as unreachable until ${timeStr}. Switchboard notified.`);
    }
  };

  const handleCall = (targetName: string) => {
    setActiveCallTarget(targetName);
    setActiveModal("callConfirm");
  };

  const handleChat = (targetName: string) => {
    history.push({
      pathname: "/client/messages",
      state: { recipient: targetName },
    });
  };

  const handleOpenPatient = (bedId: string) => {
    history.push({
      pathname: "/client/my-patients",
      state: { bedId: bedId },
    });
  };

  const handleAcceptCover = async (needId: string) => {
    const targetItem = needsItems.find((n) => n.id === needId);
    if (targetItem?.rawData?.schedule_id) {
      try {
        await NotificationsService.updateSchedules({
          sub_notification_guid: targetItem.rawData.sub_notification_guid || targetItem.rawData.guid,
          status: "ACCEPTED",
          reg_code: utilService.getNetwork(),
        });
      } catch (e) {
        // Fallback
      }
    }
    setNeedsItems((prev) =>
      prev.map((item) =>
        item.id === needId ? { ...item, status: "accepted" } : item
      )
    );
    showToast("Shift cover accepted. Your schedule has been updated.");
  };

  const handleDeclineCover = async (needId: string) => {
    const targetItem = needsItems.find((n) => n.id === needId);
    if (targetItem?.rawData?.schedule_id) {
      try {
        await NotificationsService.updateSchedules({
          sub_notification_guid: targetItem.rawData.sub_notification_guid || targetItem.rawData.guid,
          status: "DECLINED",
          reg_code: utilService.getNetwork(),
        });
      } catch (e) {
        // Fallback
      }
    }
    setNeedsItems((prev) =>
      prev.map((item) =>
        item.id === needId ? { ...item, status: "declined" } : item
      )
    );
    showToast("Shift cover request declined.");
  };

  const handleAskWhy = (item: NeedItem) => {
    history.push({
      pathname: "/client/messages",
      state: {
        recipient: item.rawData?.from_user?.name || "Doctor",
        defaultMessage: `Hi, regarding the shift request: ${item.title}...`,
      },
    });
  };

  const handleAcknowledgeNeed = async (needId: string) => {
    const targetItem = needsItems.find((n) => n.id === needId);
    if (targetItem?.rawData?.sub_notification_guid) {
      try {
        await NotificationsService.updateReadStatus({
          sub_notification_guid: targetItem.rawData.sub_notification_guid,
        });
      } catch (e) {
        // Fallback
      }
    }
    setNeedsItems((prev) =>
      prev.map((item) =>
        item.id === needId ? { ...item, status: "acknowledged" } : item
      )
    );
    showToast("Rota acknowledged.");
  };

  const handleSubmitRotaLead = () => {
    setActiveModal("none");
    showToast("Urgent alert dispatched to Rota Coordinator for coverage.");
  };

  if (isEMTUser) {
    return <EmtHomeComponent />;
  }

  const doctorDepartment = currentUser?.departmentName || "Cardiology";
  const hospitalName = currentUser?.hospitalName || "CHU Alger";

  return (
    <IonPage className="home-now-root-page">
      <IonContent scrollY={true} className="home-now-ion-content ion-no-padding">
        <div className="home-now-scroll-wrapper">
          {/* Top Sub-Header Bar */}
          <div className="now-top-subheader">
            <div className="subheader-left">
              <span className="brand-title">Nserix</span>
              <span className="now-pill">Now</span>
            </div>

            <div className="subheader-right">
              <span className="location-date-text">{formattedDateTime}</span>
            </div>
          </div>

          {/* 
            CRITICAL REQUIREMENT: "don't show the add number"
            The red "You are on call and we have no phone number for you" alert banner 
            is completely excluded here as explicitly instructed.
          */}

          {/* "On call now" Hero Card */}
          <div className="on-call-hero-card">
            <div className="hero-status-row">
              {isOnCallActive ? (
                <>
                  <span className="glowing-cyan-dot"></span>
                  <span className="status-label">On call now</span>
                </>
              ) : (
                <>
                  <span className="glowing-gray-dot"></span>
                  <span className="status-label muted">Off Duty / Next Shift</span>
                </>
              )}
              {unreachableUntil && (
                <span className="unreachable-tag">
                  Unreachable until {unreachableUntil}
                </span>
              )}
            </div>

            <h1 className="hero-title">
              {doctorDepartment} · {hospitalName}
            </h1>
            <p className="hero-subtitle">
              {isOnCallActive
                ? `Day shift, ${currentShiftHours}. You are first call for ${doctorDepartment.toLowerCase()}.`
                : `Next upcoming shift: ${currentShiftHours}. Currently not on active call.`}
            </p>

            {/* Shift Timeline Progress Bar */}
            <div className="shift-timeline-container">
              <div className="timeline-track">
                <div
                  className="timeline-progress"
                  style={{ width: `${shiftProgressPercent}%` }}
                ></div>
              </div>
              <div className="timeline-labels">
                <span className="time-label">08:00</span>
                <span className="time-label">20:00</span>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="hero-action-buttons">
              <button
                className="pill-action-btn"
                onClick={() => setActiveModal("thisShift")}
              >
                This shift
              </button>
              <button
                className="pill-action-btn"
                onClick={() => setActiveModal("handover")}
              >
                Hand over early
              </button>
              <button
                className={`pill-action-btn ${unreachableUntil ? "active" : ""}`}
                onClick={handleToggleUnreachable}
              >
                {unreachableUntil ? "Resume availability" : "Unreachable for 20 min"}
              </button>
            </div>
          </div>

          {/* Two-Column Dashboard Layout */}
          <div className="dashboard-main-grid">
            {/* LEFT COLUMN: On Call Right Now & My Patients */}
            <div className="grid-left-col">
              {/* Card 1: On call right now */}
              <div className="dash-card">
                <div className="card-header-row">
                  <span className="card-title-text">
                    On call right now - {hospitalName}
                  </span>
                  <span className="card-badge-pill">
                    <span className="pulse-dot"></span>
                    {totalGroupsCount > 0 ? `${totalGroupsCount} groups · live` : "Live roster"}
                  </span>
                </div>

                {isLoadingHome ? (
                  <div className="dash-loading-wrap">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line"></div>
                  </div>
                ) : doctorsOnCall.length > 0 ? (
                  <div className="on-call-roster-list">
                    {doctorsOnCall.map((doc) => {
                      const isUnassigned = doc.status === "unassigned";

                      return (
                        <div
                          key={doc.id}
                          className={`roster-item-row ${
                            isUnassigned ? "nobody-on-call" : ""
                          }`}
                        >
                          <div className="item-left-info">
                            {isUnassigned ? (
                              <div className="alert-hexagon-icon">
                                <IonIcon icon={alertCircleOutline} />
                              </div>
                            ) : (
                              <div className="avatar-wrap">
                                {doc.initials}
                                <span
                                  className={`status-dot ${
                                    doc.isSelf ? "green" : "green"
                                  }`}
                                ></span>
                              </div>
                            )}

                            <div className="doctor-meta">
                              <div
                                className={`doctor-name ${
                                  isUnassigned ? "danger-text" : ""
                                }`}
                              >
                                {doc.name}
                              </div>
                              <div className="specialty-subtitle">
                                {doc.specialty} · {doc.role}
                              </div>
                            </div>
                          </div>

                          <div className="item-right-actions">
                            <div className="shift-time-badge">
                              <div className="time-range">{doc.timeRange}</div>
                              <div className="shift-type">{doc.shiftType}</div>
                            </div>

                            <div className="action-btns-wrap">
                              {/* 
                                CRITICAL REQUIREMENT: "don't show the add number"
                                Even for Dr Mounir Benali (isSelf), show regular Call/Chat actions 
                                instead of any "Add Number" button!
                              */}
                              {isUnassigned ? (
                                <button
                                  className="rota-lead-btn"
                                  onClick={() => setActiveModal("rotaLead")}
                                >
                                  Tell the rota lead
                                </button>
                              ) : (
                                <>
                                  <button
                                    className="call-action-btn"
                                    title={`Call ${doc.name}`}
                                    onClick={() => handleCall(doc.name)}
                                  >
                                    <IonIcon icon={call} />
                                  </button>
                                  <button
                                    className="chat-action-btn"
                                    title={`Message ${doc.name}`}
                                    onClick={() => handleChat(doc.name)}
                                  >
                                    <IonIcon icon={chatbubbleEllipses} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-state-wrap">
                    <IonIcon icon={peopleOutline} className="empty-state-icon" />
                    <div className="empty-state-title">No doctors currently on call</div>
                    <div className="empty-state-sub">
                      No active schedules or doctors were found for this hospital department.
                    </div>
                  </div>
                )}
              </div>

              {/* Card 2: My Patient */}
              <div className="dash-card my-patients-container">
                <div className="card-header-row">
                  <span className="card-title-text">My patient</span>
                  <span className="card-badge-pill">
                    {String(myPatients.length).padStart(2, "0")}
                  </span>
                </div>

                {isLoadingPatients ? (
                  <div className="dash-loading-wrap">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line"></div>
                  </div>
                ) : myPatients.length > 0 ? (
                  <div className="patients-list">
                    {myPatients.map((patient) => (
                      <div
                        key={patient.id}
                        className="patient-row-item"
                        onClick={() => handleOpenPatient(patient.bed)}
                      >
                        <div className="patient-left">
                          <div
                            className={`bed-number-pill ${
                              patient.alertText ? "danger" : ""
                            }`}
                          >
                            {patient.bed}
                          </div>
                          <div className="patient-info-meta">
                            <div className="patient-name">{patient.name}</div>
                            <div className="patient-meta-sub">
                              {patient.department}, {patient.bed} · {patient.codeStatus} · nurse {patient.nurse}
                            </div>
                          </div>
                        </div>

                        <div className="patient-right">
                          {patient.alertText ? (
                            <span className="nurse-alert-tag">
                              {patient.alertText}
                            </span>
                          ) : (
                            <IonIcon
                              icon={chevronForwardOutline}
                              className="chevron-icon"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-wrap">
                    <IonIcon icon={bedOutline} className="empty-state-icon" />
                    <div className="empty-state-title">No patients assigned</div>
                    <div className="empty-state-sub">
                      You currently have no inpatients assigned to your rota.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: Needs you */}
            <div className="grid-right-col">
              <div className="dash-card needs-you-card">
                <div className="card-header-row">
                  <span className="card-title-text">Needs you</span>
                  <span className="card-badge-pill">
                    {needsItems.filter((n) => n.status === "pending").length} open
                  </span>
                </div>

                {isLoadingNeeds ? (
                  <div className="dash-loading-wrap">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line"></div>
                  </div>
                ) : needsItems.length > 0 ? (
                  <div className="needs-items-list">
                    {needsItems.map((item) => (
                      <div
                        key={item.id}
                        className={`need-item-block ${
                          item.status !== "pending" ? "confirmed" : ""
                        }`}
                      >
                        <div className="need-icon-col">
                          <div className={`circle-icon-wrap ${item.iconType}`}>
                            <IonIcon
                              icon={
                                item.type === "cover"
                                  ? swapHorizontalOutline
                                  : item.type === "patient_alert"
                                  ? alertCircleOutline
                                  : timeOutline
                              }
                            />
                          </div>
                        </div>

                        <div className="need-content-col">
                          <div className="need-headline">{item.title}</div>
                          <div className="need-timestamp">{item.meta}</div>

                          <div className="action-buttons-group">
                            {item.status === "pending" ? (
                              item.type === "cover" ? (
                                <>
                                  <button
                                    className="btn-blue"
                                    onClick={() => handleAcceptCover(item.id)}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    className="btn-subtle"
                                    onClick={() => handleDeclineCover(item.id)}
                                  >
                                    Decline
                                  </button>
                                  <button
                                    className="btn-subtle"
                                    onClick={() => handleAskWhy(item)}
                                  >
                                    Ask why
                                  </button>
                                </>
                              ) : item.type === "patient_alert" ? (
                                <>
                                  <button
                                    className="btn-red"
                                    onClick={() => history.push("/client/my-patients")}
                                  >
                                    Open patient
                                  </button>
                                  <button
                                    className="btn-subtle"
                                    onClick={() => history.push("/client/messages")}
                                  >
                                    Read the thread
                                  </button>
                                </>
                              ) : (
                                <button
                                  className="btn-subtle"
                                  onClick={() => handleAcknowledgeNeed(item.id)}
                                >
                                  Acknowledge
                                </button>
                              )
                            ) : (
                              <span className="confirmed-pill" style={{ color: "#10b981", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                <IonIcon icon={checkmarkCircleOutline} />
                                {item.status === "accepted"
                                  ? "Accepted"
                                  : item.status === "declined"
                                  ? "Declined"
                                  : "Acknowledged"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-wrap">
                    <IonIcon icon={shieldCheckmarkOutline} className="empty-state-icon" style={{ color: "#10b981" }} />
                    <div className="empty-state-title">All caught up!</div>
                    <div className="empty-state-sub">
                      No pending coverage requests or urgent clinical alerts.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Interactive Modal: This Shift */}
          {activeModal === "thisShift" && (
            <div
              className="custom-dashboard-modal-backdrop"
              onClick={() => setActiveModal("none")}
            >
              <div
                className="custom-modal-window"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Shift Details</h3>
                  <button
                    className="close-btn"
                    onClick={() => setActiveModal("none")}
                  >
                    <IonIcon icon={closeOutline} />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="detail-row">
                    <span className="label">Department:</span>
                    <span className="value">{doctorDepartment}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Hospital:</span>
                    <span className="value">{hospitalName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Shift Hours:</span>
                    <span className="value">{currentShiftHours}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Role:</span>
                    <span className="value">First Call Cardiology</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Status:</span>
                    <span className="value" style={{ color: "#10b981" }}>
                      Active
                    </span>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-cancel"
                    onClick={() => setActiveModal("none")}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Interactive Modal: Hand Over Early */}
          {activeModal === "handover" && (
            <div
              className="custom-dashboard-modal-backdrop"
              onClick={() => setActiveModal("none")}
            >
              <div
                className="custom-modal-window"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Hand Over Early</h3>
                  <button
                    className="close-btn"
                    onClick={() => setActiveModal("none")}
                  >
                    <IonIcon icon={closeOutline} />
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    Initiate clinical handover before 20:00. This will transfer first-call
                    pager and alerts to the incoming colleague.
                  </p>
                  <div className="detail-row">
                    <span className="label">Incoming Colleague:</span>
                    <span className="value">Dr Amine John (Neurology)</span>
                  </div>
                  <textarea
                    placeholder="Enter handover briefing notes or patient watchpoints..."
                    defaultValue="Handover summary: Bed 103 stable, waiting troponin repeat."
                  />
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-cancel"
                    onClick={() => setActiveModal("none")}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-confirm"
                    onClick={() => {
                      setActiveModal("none");
                      showToast("Clinical handover successfully completed.");
                    }}
                  >
                    Confirm Handover
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Interactive Modal: Tell the Rota Lead */}
          {activeModal === "rotaLead" && (
            <div
              className="custom-dashboard-modal-backdrop"
              onClick={() => setActiveModal("none")}
            >
              <div
                className="custom-modal-window"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Alert Rota Coordinator</h3>
                  <button
                    className="close-btn"
                    onClick={() => setActiveModal("none")}
                  >
                    <IonIcon icon={closeOutline} />
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ color: "#f87171" }}>
                    There is currently nobody on call for this group. An urgent broadcast
                    will be sent to the on-call rota coordinator.
                  </p>
                  <textarea
                    placeholder="Add specific urgency note..."
                    value={rotaLeadNote}
                    onChange={(e) => setRotaLeadNote(e.target.value)}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-cancel"
                    onClick={() => setActiveModal("none")}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-confirm"
                    style={{ background: "#ef4444" }}
                    onClick={handleSubmitRotaLead}
                  >
                    Dispatch Alert
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Interactive Modal: Secure Call */}
          {activeModal === "callConfirm" && (
            <div
              className="custom-dashboard-modal-backdrop"
              onClick={() => setActiveModal("none")}
            >
              <div
                className="custom-modal-window"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Start Secure VoIP Call</h3>
                  <button
                    className="close-btn"
                    onClick={() => setActiveModal("none")}
                  >
                    <IonIcon icon={closeOutline} />
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    Connect direct encrypted clinical call with{" "}
                    <strong>{activeCallTarget}</strong> via Nserix PBX network.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-cancel"
                    onClick={() => setActiveModal("none")}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-confirm"
                    style={{ background: "#10b981" }}
                    onClick={() => {
                      setActiveModal("none");
                      showToast(`Calling ${activeCallTarget}...`);
                    }}
                  >
                    Call Now
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Toast Notification */}
          {toastMessage && (
            <div className="dashboard-toast">{toastMessage}</div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Home;
